import { useState } from 'react'
import type { ChangeEvent, FormEvent, ReactNode } from 'react'
import { LoaderCircle, Send, Server } from 'lucide-react'
import { DiscordIcon, SteamIcon } from '@/components/BrandIcons'
import Button, { AnchorButton } from '@/components/base/Button'
import SectionHeading from '@/components/feature/SectionHeading'
import useCommunity from '@/hooks/useCommunity'
import serverConfig from '@/config/serverConfig'
import { useLanguage } from '@/localization'

type SubmitStatus = 'idle' | 'success' | 'error'

interface FormData {
  name: string
  email: string
  subject: string
  message: string
}

const initialForm: FormData = {
  name: '',
  email: '',
  subject: '',
  message: '',
}

const subjects = [
  ['general', 'General Inquiry'],
  ['support', 'Technical Support'],
  ['vanilla', 'Survival Server'],
  ['modded', 'Modded Server'],
  ['modpack', 'Modpack Issue'],
  ['report', 'Report Player'],
  ['suggestion', 'Suggestion'],
  ['other', 'Other'],
]

export default function ContactSection() {
  const { discordInvite, social } = useCommunity()
  const { t } = useLanguage()
  const steamHref =
    social.find((item) => item.icon === 'steam')?.href ?? serverConfig.community.steam
  const [formData, setFormData] = useState<FormData>(initialForm)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState<SubmitStatus>('idle')

  function handleInputChange(
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) {
    const { name, value } = event.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()

    if (formData.message.length > 500) {
      setSubmitStatus('error')
      return
    }

    setIsSubmitting(true)
    setSubmitStatus('idle')

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, site: 'minecraft' }),
      })

      if (!response.ok) throw new Error(`HTTP ${response.status}`)

      setSubmitStatus('success')
      setFormData(initialForm)
    } catch {
      setSubmitStatus('error')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <section id="contact" className="mx-auto w-full max-w-6xl scroll-mt-20 px-4 py-16 sm:py-20">
      <SectionHeading
        eyebrow="Contact"
        title="Send a Message"
        subtitle="Questions, reports, modpack issues or suggestions. Send it here and it reaches the Uliunai team."
      />

      <div className="grid gap-8 lg:grid-cols-[minmax(0,1.2fr)_minmax(320px,0.8fr)]">
        <form
          id="contact-form"
          onSubmit={handleSubmit}
          className="rounded-2xl border border-stone-light/70 bg-white p-6 shadow-block-lg sm:p-8"
        >
          <h3 className="mb-6 text-2xl font-extrabold text-slate-deep">{t('Send us a Message')}</h3>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Name *" id="mc-contact-name">
              <input
                id="mc-contact-name"
                name="name"
                type="text"
                value={formData.name}
                onChange={handleInputChange}
                required
                placeholder={t('Your name')}
                className={inputClass}
              />
            </Field>
            <Field label="Email *" id="mc-contact-email">
              <input
                id="mc-contact-email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                placeholder={t('your@email.com')}
                className={inputClass}
              />
            </Field>
          </div>

          <Field label="Subject *" id="mc-contact-subject" className="mt-4">
            <select
              id="mc-contact-subject"
              name="subject"
              value={formData.subject}
              onChange={handleInputChange}
              required
              className={inputClass}
            >
              <option value="">{t('Select a subject')}</option>
              {subjects.map(([value, label]) => (
                <option key={value} value={value}>
                  {t(label)}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Message * (Max 500 characters)" id="mc-contact-message" className="mt-4">
            <textarea
              id="mc-contact-message"
              name="message"
              value={formData.message}
              onChange={handleInputChange}
              required
              maxLength={500}
              rows={6}
              placeholder={t('Your message here...')}
              className={`${inputClass} resize-none`}
            />
            <div className="mt-1 text-right text-xs font-bold text-slate-deep/45">
              {formData.message.length}/500 {t('characters')}
            </div>
          </Field>

          <Button
            type="submit"
            variant="primary"
            size="lg"
            disabled={isSubmitting}
            className="mt-6 w-full"
          >
            {isSubmitting ? (
              <>
                <LoaderCircle size={18} className="animate-spin" />
                {t('Sending...')}
              </>
            ) : (
              <>
                <Send size={18} />
                {t('Send Message')}
              </>
            )}
          </Button>

          {submitStatus === 'success' && (
            <p className="mt-4 text-center font-bold text-grass-deep">{t('Message sent successfully.')}</p>
          )}
          {submitStatus === 'error' && (
            <p className="mt-4 text-center font-bold text-ul-red">
              {t('Failed to send message. Please try again or use Discord.')}
            </p>
          )}
        </form>

        <div className="space-y-5">
          <InfoCard
            icon={<DiscordIcon size={22} />}
            title="Discord Server"
            body="Fastest help for server issues and launch updates."
            tone="sky"
          >
            <AnchorButton href={discordInvite} target="_blank" rel="noopener noreferrer" variant="sky">
              <DiscordIcon size={16} /> Open Discord
            </AnchorButton>
          </InfoCard>
          <InfoCard
            icon={<Server size={22} />}
            title="Server Addresses"
            body="Survival is live; modded remains a separate planned server."
            tone="grass"
          >
            <div className="space-y-1 text-sm font-bold text-slate-deep/70">
              <p>{serverConfig.servers.vanilla.host}</p>
              <p>{serverConfig.servers.modded.host}</p>
            </div>
          </InfoCard>
          <InfoCard
            icon={<SteamIcon size={22} />}
            title="Steam Group"
            body="Follow the wider Uliunai community."
            tone="wood"
          >
            <a
              href={steamHref}
              target="_blank"
              rel="noopener noreferrer"
              className="font-bold text-wood-dark transition-colors hover:text-wood"
            >
              steamcommunity.com/groups/uliunailt
            </a>
          </InfoCard>
        </div>
      </div>
    </section>
  )
}

const inputClass =
  'w-full rounded-xl border border-stone-light bg-[#f4f7f2] px-4 py-3 text-sm font-semibold text-slate-deep placeholder:text-slate-deep/35 shadow-inner transition-colors focus:border-grass-dark focus:bg-white focus:outline-none focus:ring-2 focus:ring-grass/30'

function Field({
  label,
  id,
  className = '',
  children,
}: {
  label: string
  id: string
  className?: string
  children: ReactNode
}) {
  const { t } = useLanguage()

  return (
    <div className={className}>
      <label htmlFor={id} className="mb-2 block text-sm font-extrabold text-slate-deep/75">
        {t(label)}
      </label>
      {children}
    </div>
  )
}

function InfoCard({
  icon,
  title,
  body,
  tone,
  children,
}: {
  icon: ReactNode
  title: string
  body: string
  tone: 'grass' | 'sky' | 'wood'
  children: ReactNode
}) {
  const { t } = useLanguage()
  const toneClass = {
    grass: 'bg-grass-gradient text-white',
    sky: 'bg-sky-gradient text-slate-deep',
    wood: 'bg-wood-gradient text-white',
  }[tone]

  return (
    <div className="rounded-2xl border border-stone-light/70 bg-white p-6 shadow-block">
      <div className="flex gap-4">
        <span className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl shadow-block ${toneClass}`}>
          {icon}
        </span>
        <div className="min-w-0">
          <h3 className="text-lg font-extrabold text-slate-deep">{t(title)}</h3>
          <p className="mb-3 text-slate-deep/65">{t(body)}</p>
          {children}
        </div>
      </div>
    </div>
  )
}
